import * as React from "react";
import clsx from "clsx";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ChevronDown, Flag, GripVertical, Pencil, Trash2 } from "lucide-react";
import { Button } from "../../../ui/components/Button";
import { useI18n } from "../../lib/i18n";
import type { RoadmapItem } from "../../lib/api/roadmap";
import * as s from "./RoadmapCard.css";

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 31_536_000],
  ["month", 2_592_000],
  ["week", 604_800],
  ["day", 86_400],
  ["hour", 3_600],
  ["minute", 60],
];

function formatRelative(iso: string, locale: string): string {
  const seconds = Math.round((new Date(iso).getTime() - Date.now()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  for (const [unit, size] of RELATIVE_UNITS) {
    if (Math.abs(seconds) >= size) return rtf.format(Math.round(seconds / size), unit);
  }
  return rtf.format(seconds, "second");
}

function formatExact(iso: string, locale: string): string {
  return new Date(iso).toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" });
}

const PRIORITY_CLASS = {
  p1: s.priorityP1,
  p2: s.priorityP2,
  p3: s.priorityP3,
} as const;

interface RoadmapCardViewProps {
  item: RoadmapItem;
  expanded?: boolean;
  onToggle?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  handleProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
  handleRef?: (element: HTMLElement | null) => void;
  overlay?: boolean;
}

export function RoadmapCardView({
  item,
  expanded = false,
  onToggle,
  onEdit,
  onDelete,
  handleProps,
  handleRef,
  overlay = false,
}: RoadmapCardViewProps) {
  const { t, language } = useI18n();

  return (
    <div className={clsx(s.card, overlay && s.cardOverlay)}>
      <div className={s.header}>
        <button
          ref={handleRef}
          type="button"
          className={s.handle}
          aria-label={t("pro.system.roadmap.card.drag")}
          {...handleProps}
        >
          <GripVertical size={14} />
        </button>
        <button
          type="button"
          className={s.titleButton}
          onClick={onToggle}
          aria-expanded={expanded}
        >
          <span className={s.name}>{item.name}</span>
          <ChevronDown size={14} className={clsx(s.chevron, expanded && s.chevronOpen)} />
        </button>
      </div>

      {(item.priority || item.complexity || item.category) && (
        <div className={s.meta}>
          {item.priority && (
            <span className={s.chip}>
              <Flag size={11} className={PRIORITY_CLASS[item.priority]} />
              {t(`pro.system.roadmap.priority.${item.priority}`)}
            </span>
          )}
          {item.complexity && (
            <span className={s.chip}>{t(`pro.system.roadmap.complexity.${item.complexity}`)}</span>
          )}
          {item.category && <span className={s.category}>{item.category}</span>}
        </div>
      )}

      {expanded && (
        <div className={s.details}>
          {item.description ? (
            <p className={s.description}>{item.description}</p>
          ) : (
            <p className={s.detailRow}>{t("pro.system.roadmap.card.noDescription")}</p>
          )}
          {item.origin && (
            <p className={s.detailRow}>
              <span className={s.detailLabel}>{t("pro.system.roadmap.field.origin")}: </span>
              {t(`pro.system.roadmap.origin.${item.origin}`)}
            </p>
          )}
          {item.smartSource && (
            <p className={s.detailRow}>
              <span className={s.detailLabel}>{t("pro.system.roadmap.field.smartSource")}: </span>
              {item.smartSource}
            </p>
          )}
          <p className={s.detailRow}>
            <span className={s.detailLabel}>{t("pro.system.roadmap.field.createdBy")}: </span>
            {item.createdByName ?? t("pro.system.roadmap.card.createdBySeed")}
          </p>
          <p className={s.detailRow}>
            <span className={s.detailLabel}>{t("pro.system.roadmap.field.created")}: </span>
            <time dateTime={item.createdAt} title={formatExact(item.createdAt, language)} className={s.relativeTime}>
              {formatRelative(item.createdAt, language)}
            </time>
          </p>
          <div className={s.actions}>
            <Button
              size="sm"
              variant="ghost"
              color="neutral"
              iconLeft={<Pencil size={14} />}
              onClick={onEdit}
            >
              {t("pro.system.roadmap.card.edit")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              color="danger"
              iconLeft={<Trash2 size={14} />}
              onClick={onDelete}
            >
              {t("pro.system.roadmap.card.delete")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

interface RoadmapCardProps {
  item: RoadmapItem;
  onEdit: (item: RoadmapItem) => void;
  onDelete: (item: RoadmapItem) => void;
}

export function RoadmapCard({ item, onEdit, onDelete }: RoadmapCardProps) {
  const [expanded, setExpanded] = React.useState(false);
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.id });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={clsx(isDragging && s.cardDragging)}
    >
      <RoadmapCardView
        item={item}
        expanded={expanded}
        onToggle={() => setExpanded((v) => !v)}
        onEdit={() => onEdit(item)}
        onDelete={() => onDelete(item)}
        handleRef={setActivatorNodeRef}
        handleProps={{ ...attributes, ...listeners }}
      />
    </div>
  );
}
